import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import {
  FARK_SEBEBI_ETIKETI,
  donemListesi,
  mutabakatAc,
  mutabakatOzeti,
  mutabakatTrendi,
  muhasebeTutariGir,
  type FarkSebebi,
  type MutabakatTrendSatiri,
} from '../api/donem';
import { Diyalog } from '../bilesenler/Diyalog';
import { tutarMetni } from '../bilesenler/DonemParcalari';
import { Tablo, type Sutun } from '../bilesenler/Tablo';
import { CokSatir, Dugme, HataKutusu, Metin } from '../bilesenler/temel';
import { tarihMetni } from '../bicim/tarih';
import { yetkiKullan } from '../yetki/yetki';

/**
 * MUTABAKAT — sistem tutarı ile muhasebe tutarının karşılaştırılması.
 *
 * SİSTEM TUTARI ÖNCE DONDURULUR, muhasebe tutarı SONRA girilir. Ters sırada
 * olsaydı sistem tutarı muhasebeye göre "düzeltilebilir" ve mutabakat anlamını
 * yitirirdi. Panel bu sırayı zorunlu kılar: kayıt açılmadan tutar alanı
 * görünmez.
 *
 * FARK HER AY AYNI YÖNDE İSE SİSTEMATİK HATA VAR ve bulunabilir; trend tablosu
 * bunun için var.
 */

/** Sunucudaki `@Matches` deseniyle AYNI — kullanıcı 400 yerine anında uyarı görsün. */
const TUTAR_DESENI = /^\d{1,14}(\.\d{1,2})?$/;

export function DonemMutabakat() {
  const girebilir = yetkiKullan('MUTABAKAT_GIR');
  const acabilir = yetkiKullan('MUTABAKAT_GIR', 'DONEM_KAPAT');

  const [donemId, donemIdYaz] = useState<number | ''>('');
  const [formAcik, formAcikYaz] = useState(false);

  const donemler = useQuery({ queryKey: ['donem', 'liste'], queryFn: () => donemListesi() });
  const trend = useQuery({ queryKey: ['donem', 'trend'], queryFn: () => mutabakatTrendi() });

  const mutabakat = useQuery({
    queryKey: ['donem', 'mutabakat', donemId],
    queryFn: () => mutabakatOzeti(Number(donemId)),
    enabled: donemId !== '',
    // Kayıt henüz açılmamışsa sunucu 404 döndürüyor; tekrar denemek anlamsız.
    retry: false,
  });

  const sorguIstemcisi = useQueryClient();

  const ac = useMutation({
    mutationFn: () => mutabakatAc(Number(donemId)),
    onSuccess: () => sorguIstemcisi.invalidateQueries({ queryKey: ['donem', 'mutabakat'] }),
  });

  return (
    <div className="space-y-6">
      <label className="block max-w-xs">
        <span className="mb-1 block text-sm font-medium text-slate-700">Dönem</span>
        <select
          value={donemId}
          onChange={(olay) => donemIdYaz(olay.target.value === '' ? '' : Number(olay.target.value))}
          className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-kurum-500 focus:ring-2 focus:ring-kurum-100"
        >
          <option value="">Seçin…</option>
          {(donemler.data ?? []).map((d) => (
            <option key={d.donemId} value={d.donemId}>
              {tarihMetni(d.donemAy)}
            </option>
          ))}
        </select>
      </label>

      {donemId !== '' && (
        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-slate-800">Mutabakat durumu</h2>

          {mutabakat.isPending ? (
            <p className="text-sm text-slate-500">Yükleniyor…</p>
          ) : mutabakat.data === undefined ? (
            <div className="rounded-md border border-slate-200 bg-white p-4">
              <p className="text-sm text-slate-700">
                Bu dönem için mutabakat kaydı açılmamış. Kayıt açıldığında{' '}
                <strong>sistem tutarı dondurulur</strong>; muhasebe tutarı sonra girilir.
              </p>
              <HataKutusu hata={ac.error} />
              {acabilir && (
                <div className="mt-3">
                  <Dugme
                    tikla={() => ac.mutate()}
                    bekliyor={ac.isPending}
                    cocuk="Mutabakat kaydı aç"
                  />
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3 rounded-md border border-slate-200 bg-white p-4">
              <dl className="grid gap-3 text-sm sm:grid-cols-3">
                <Kutu
                  etiket="Sistem (brüt)"
                  deger={tutarMetni(mutabakat.data.sistemBrutMesai)}
                  not="Dondurulmuş"
                />
                <Kutu
                  etiket="Muhasebe (brüt)"
                  deger={tutarMetni(mutabakat.data.muhasebeBrutMesai)}
                  not={mutabakat.data.muhasebeBrutMesai === null ? 'Girilmedi' : undefined}
                />
                <Kutu
                  etiket="Fark"
                  deger={tutarMetni(mutabakat.data.fark)}
                  not={
                    mutabakat.data.farkOrani === null
                      ? undefined
                      : `oran ${mutabakat.data.farkOrani}`
                  }
                />
              </dl>

              {mutabakat.data.farkSebebi !== null && (
                <p className="text-sm text-slate-700">
                  Fark sebebi: <strong>{FARK_SEBEBI_ETIKETI[mutabakat.data.farkSebebi]}</strong>
                  {mutabakat.data.aciklama !== null && ` · ${mutabakat.data.aciklama}`}
                </p>
              )}

              {girebilir && (
                <Dugme
                  tur="ikincil"
                  tikla={() => formAcikYaz(true)}
                  cocuk={
                    mutabakat.data.muhasebeBrutMesai === null
                      ? 'Muhasebe tutarını gir'
                      : 'Tutarı güncelle'
                  }
                />
              )}
            </div>
          )}
        </section>
      )}

      <section className="space-y-2">
        <h2 className="text-sm font-semibold text-slate-800">Mutabakat trendi</h2>
        <p className="text-xs text-slate-500">
          Fark her ay AYNI YÖNDE ise sistematik hata vardır ve bulunabilir. Hiç ölçülmezse sistem
          yıllarca yanlış rakam üretir.
        </p>
        <HataKutusu hata={trend.error} />
        <TrendTablosu satirlar={trend.data ?? []} bekliyor={trend.isPending} />
      </section>

      {formAcik && donemId !== '' && (
        <TutarDiyalogu donemId={Number(donemId)} kapat={() => formAcikYaz(false)} />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */

const YON_ETIKETI: Record<MutabakatTrendSatiri['farkYonu'], string> = {
  muhasebe_fazla: 'Muhasebe fazla',
  sistem_fazla: 'Sistem fazla',
  esit: 'Eşit',
};

const YON_BICIMI: Record<MutabakatTrendSatiri['farkYonu'], string> = {
  muhasebe_fazla: 'bg-amber-50 text-amber-800',
  sistem_fazla: 'bg-sky-50 text-sky-700',
  esit: 'bg-emerald-50 text-emerald-700',
};

const TREND_SUTUNLARI: Sutun<MutabakatTrendSatiri>[] = [
  { baslik: 'Dönem', hucre: (t) => tarihMetni(t.donemAy), daralt: true },
  { baslik: 'Sistem', hucre: (t) => tutarMetni(t.sistemBrutMesai), daralt: true, hizala: 'sag' },
  {
    baslik: 'Muhasebe',
    hucre: (t) => tutarMetni(t.muhasebeBrutMesai),
    daralt: true,
    hizala: 'sag',
  },
  { baslik: 'Fark', hucre: (t) => tutarMetni(t.fark), daralt: true, hizala: 'sag' },
  {
    baslik: 'Yüzde',
    // METİN — eşik karşılaştırması sunucuda, panel yalnız basar.
    hucre: (t) => (t.farkYuzde === null ? '—' : `%${t.farkYuzde}`),
    daralt: true,
    hizala: 'sag',
  },
  {
    baslik: 'Yön',
    /*
      `farkYonu` AYRI ALAN: tutarın işaretini her tüketici kendi okusaydı yön
      hesabı tekrarlanır ve biri yanlış yapardı.
    */
    hucre: (t) => (
      <span
        className={`whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${YON_BICIMI[t.farkYonu]}`}
      >
        {YON_ETIKETI[t.farkYonu]}
      </span>
    ),
    daralt: true,
  },
  {
    baslik: 'Sebep',
    hucre: (t) => (t.farkSebebi === null ? '—' : FARK_SEBEBI_ETIKETI[t.farkSebebi]),
    daralt: true,
  },
];

function TrendTablosu({
  satirlar,
  bekliyor,
}: {
  satirlar: MutabakatTrendSatiri[];
  bekliyor: boolean;
}) {
  return (
    <Tablo
      sutunlar={TREND_SUTUNLARI}
      satirlar={satirlar}
      anahtar={(t) => t.donemAy}
      bekliyor={bekliyor}
      bosMetin="Henüz muhasebe tutarı girilmiş dönem yok."
    />
  );
}

/* ------------------------------------------------------------------ */

function TutarDiyalogu({ donemId, kapat }: { donemId: number; kapat: () => void }) {
  const sorguIstemcisi = useQueryClient();

  const [tutar, tutarYaz] = useState('');
  const [farkSebebi, farkSebebiYaz] = useState<FarkSebebi | ''>('');
  const [aciklama, aciklamaYaz] = useState('');

  const gir = useMutation({
    mutationFn: () =>
      muhasebeTutariGir(
        donemId,
        tutar.trim(),
        farkSebebi === '' ? undefined : farkSebebi,
        aciklama.trim() === '' ? undefined : aciklama.trim(),
      ),
    onSuccess: async () => {
      await sorguIstemcisi.invalidateQueries({ queryKey: ['donem'] });
      kapat();
    },
  });

  const bicimGecerli = TUTAR_DESENI.test(tutar.trim());

  return (
    <Diyalog
      baslik="Muhasebe tutarını gir"
      aciklama="BRÜT tutar girilir. Net karşılaştırma her dönem yanlış alarm üretir."
      kapat={kapat}
      cocuk={
        <div className="space-y-4">
          <Metin
            etiket="Muhasebe brüt mesai tutarı"
            deger={tutar}
            degisti={tutarYaz}
            ipucu="Örn. 284350.00 — ondalık ayracı NOKTA, en çok iki basamak."
          />

          {/* Biçim panelde de denetlenir; sunucu `@Matches` ile 400 döndürürdü. */}
          {tutar.trim() !== '' && !bicimGecerli && (
            <p className="text-xs text-red-600">
              Biçim geçersiz. Örnek: <code>284350.00</code>
            </p>
          )}

          <label className="block">
            <span className="mb-1 block text-sm font-medium text-slate-700">
              Fark sebebi (eşik aşılırsa zorunlu)
            </span>
            <select
              value={farkSebebi}
              onChange={(olay) => farkSebebiYaz(olay.target.value as FarkSebebi | '')}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-kurum-500 focus:ring-2 focus:ring-kurum-100"
            >
              <option value="">Seçilmedi</option>
              {(Object.keys(FARK_SEBEBI_ETIKETI) as FarkSebebi[]).map((s) => (
                <option key={s} value={s}>
                  {FARK_SEBEBI_ETIKETI[s]}
                </option>
              ))}
            </select>
            <span className="mt-1 block text-xs text-slate-500">
              Eşik aşılmışsa sunucu sebep olmadan kaydı reddeder ve dönem kilitlenemez.
            </span>
          </label>

          <CokSatir
            etiket="Açıklama (isteğe bağlı)"
            deger={aciklama}
            degisti={aciklamaYaz}
            satir={2}
          />

          <HataKutusu hata={gir.error} />

          <div className="flex justify-end gap-2">
            <Dugme tur="ikincil" tikla={kapat} pasif={gir.isPending} cocuk="Vazgeç" />
            <Dugme
              tikla={() => gir.mutate()}
              bekliyor={gir.isPending}
              pasif={!bicimGecerli}
              cocuk="Kaydet"
            />
          </div>
        </div>
      }
    />
  );
}

function Kutu({ etiket, deger, not }: { etiket: string; deger: string; not?: string }) {
  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
      <dt className="text-xs text-slate-500">{etiket}</dt>
      <dd className="mt-0.5 font-medium text-slate-900">{deger}</dd>
      {not !== undefined && <dd className="text-xs text-slate-500">{not}</dd>}
    </div>
  );
}
